import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SavingsInsightsSummaryDto } from 'entities/ReportSavingsSummary';

interface SavingsSummaryTotalSavingsMetricProps {
    summary: SavingsInsightsSummaryDto | null;
    primaryAmount: string | number | null;
    loading?: boolean;
}

export const SavingsSummaryTotalSavingsMetric = memo(
    ({
        summary,
        primaryAmount,
        loading = false,
    }: SavingsSummaryTotalSavingsMetricProps) => {
        const { t } = useTranslation();

        const savingsPercentLabel = summary
            ? formatReportPercent(summary.savingsPercentage)
            : '';

        return (
            <ReportMetric
                title={t('savingsSummary.totalSavings')}
                icon={<AttachMoneyIcon fontSize="small" />}
                primary={
                    primaryAmount == null
                        ? '—'
                        : formatReportCurrency(primaryAmount)
                }
                primaryColor="rgb(22, 163, 74)"
                primaryTooltip={t('savingsSummary.totalSavingsTooltip')}
                secondary={
                    savingsPercentLabel
                        ? `(${savingsPercentLabel} ${t(
                              'savingsSummary.savedSuffix',
                          )})`
                        : undefined
                }
                secondarySx={{
                    color: 'rgb(22, 163, 74)',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 500,
                }}
                loading={loading}
                minHeight="100px"
                sx={{
                    minWidth: '240px',
                }}
            >
                <Box
                    sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <span>{t('savingsSummary.totalSpend')}</span>
                    <Box
                        component="span"
                        sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600,
                        }}
                    >
                        {summary
                            ? formatReportCurrency(summary.totalSpendAll)
                            : '—'}
                    </Box>
                </Box>
                <Box
                    sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <span>{t('savingsSummary.competitiveSpend')}</span>
                        <DynamicTooltip
                            content={t(
                                'savingsSummary.competitiveSpendTooltip',
                            )}
                            containerStyle={{
                                height: '16px',
                                paddingTop: '1px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 14,
                                    color: 'text.disabled',
                                }}
                                data-pdf-tooltip="true"
                            />
                        </DynamicTooltip>
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600,
                        }}
                    >
                        {summary
                            ? formatReportCurrency(summary.totalSpend)
                            : '—'}
                    </Box>
                </Box>
                <Box
                    sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <span>{t('savingsSummary.avgSupplierSpend')}</span>
                    <Box
                        component="span"
                        sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600,
                        }}
                    >
                        {summary?.avgSupplierSpend == null
                            ? '—'
                            : formatReportCurrency(summary.avgSupplierSpend)}
                    </Box>
                </Box>
            </ReportMetric>
        );
    },
);

SavingsSummaryTotalSavingsMetric.displayName =
    'SavingsSummaryTotalSavingsMetric';
